import AuthForm from '@/components/features/auth/AuthForm'
import AuthPage from '@/components/features/auth/AuthPage'
import {
  ConfirmationCodeController,
  CredentialController,
} from '@/components/forms/FormController'
import ErrorMessage from '@/components/utils/ErrorMessage'
import { useAuth } from '@/context/AuthContext'
import { confirmationCodeSchema } from '@/lib/confirmation-code'
import { zodResolver } from '@/lib/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@droidmap/frontend-ui'

export const Route = createFileRoute('/_unauthenticated/forgot-password')({
  component: ForgotPassword,
})

const requestCodeSchema = z.object({
  email: z.string().email('Invalid email address'),
})

type RequestCodeFormValues = z.infer<typeof requestCodeSchema>

const confirmCodeSchema = z.object({
  email: z.string().email('Invalid email address'),
  code: confirmationCodeSchema,
  newPassword: z.string().min(12, 'Password must be at least 12 characters'),
})

type ConfirmCodeFormValues = z.infer<typeof confirmCodeSchema>

export function ForgotPassword() {
  const [error, setError] = useState<string | undefined>(undefined)
  const [generateCodeSuccess, setGenerateCodeSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const auth = useAuth()
  const navigate = useNavigate()

  // Get URL parameters for auto-populating from email link
  const { email, code } = Route.useSearch() as {
    email?: string
    code?: string | number
  }

  // If we have both email and code from URL, skip to confirm form
  const hasUrlParams = !!(email && code)

  // Request code form
  const {
    control: requestControl,
    handleSubmit: handleRequestSubmit,
    formState: { errors: requestErrors },
  } = useForm<RequestCodeFormValues>({
    resolver: zodResolver(requestCodeSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  })

  // Confirm code form
  const {
    control: confirmControl,
    handleSubmit: handleConfirmSubmit,
    formState: { errors: confirmErrors },
  } = useForm<ConfirmCodeFormValues>({
    resolver: zodResolver(confirmCodeSchema),
    defaultValues: {
      email: typeof email === 'string' ? email : '',
      code: code != null ? String(code) : '',
      newPassword: '',
    },
    mode: 'onBlur',
  })

  const onRequestSubmit = async (data: RequestCodeFormValues) => {
    setError(undefined)
    setLoading(true)
    try {
      await auth.forgotPassword(data.email)
      setGenerateCodeSuccess(true)
      // Set email in confirm form
      confirmControl._reset({ email: data.email, code: '', newPassword: '' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  // Determine if we should show the confirm form
  const showConfirmForm = generateCodeSuccess || hasUrlParams

  const onConfirmSubmit = async (data: ConfirmCodeFormValues) => {
    setError(undefined)
    setLoading(true)
    try {
      await auth.confirmPassword(data.email, data.code, data.newPassword)
      navigate({ to: '/' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthPage title="Forgot your password?">
      {showConfirmForm ? (
        <AuthForm onSubmit={handleConfirmSubmit(onConfirmSubmit)}>
          {!hasUrlParams && (
            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
              <div className="flex justify-center text-sm font-medium leading-6 text-text">
                <p>Please check your inbox for the confirmation code.</p>
              </div>
            </div>
          )}
          <CredentialController
            name="email"
            control={confirmControl}
            label="Email address"
            errors={confirmErrors}
            disabled={generateCodeSuccess || hasUrlParams}
            autoComplete="email"
          />
          {/* Same field, same route family as confirm-signup — so the same
              controller, carrying the ignore attributes, the one-time-code
              hint and the six-digit shape constraint. (#2848, #2882) */}
          <ConfirmationCodeController
            name="code"
            control={confirmControl}
            label="6-digit confirmation code"
            errors={confirmErrors}
          />
          <CredentialController
            name="newPassword"
            control={confirmControl}
            label="New password (at least 12 characters)"
            errors={confirmErrors}
            type="password"
            autoComplete="new-password"
          />
          {error && <ErrorMessage content={error} />}
          <Button type="submit" isLoading={loading} disabled={loading}>
            Reset my password
          </Button>
        </AuthForm>
      ) : (
        <AuthForm onSubmit={handleRequestSubmit(onRequestSubmit)}>
          <CredentialController
            name="email"
            control={requestControl}
            label="Email address"
            errors={requestErrors}
            autoComplete="email"
          />
          {error && <ErrorMessage content={error} />}
          <Button
            type="submit"
            className="w-full"
            isLoading={loading}
            disabled={loading}
          >
            Reset my password
          </Button>
        </AuthForm>
      )}
    </AuthPage>
  )
}
